import { useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbars } from '@customerio/pluma-components/react';
import type { ObjectAttributesEditorState } from '@/features/objects';
import { useAnalytics } from '@/services/analytics';
import type { Customer } from '@/services/db/customer/entity';
import type { ObjectRecord } from '@/services/db/object/entity';
import { objectTypeKeys, updateObjectTypeSentRelAttributes, type ObjectType } from '@/services/db/object-type/entity';
import { useCreateRelationships } from '@/services/db/relationship/mutations';
import { relationshipInputs } from './relationship-inputs';

const SNACKBAR_ID = 'relationships.add';

type SubmitOptions = {
  workspaceId: number;
  objectType: ObjectType;
  /** Analytics source, e.g. 'Object List' or 'Object Profile'. */
  source: string;
  people: readonly Customer[];
  objects: readonly ObjectRecord[];
  editor: ObjectAttributesEditorState;
  onConfirm: () => void;
};

export type RelationshipSubmit = {
  submit: () => void;
  isSubmitting: boolean;
};

/**
 * Creates the relationships and everything that follows a successful write:
 * the confirmation, the completion event, and folding the attribute keys into
 * the object type's relationship-attribute catalog, which the API lags behind.
 */
export function useRelationshipSubmit(options: SubmitOptions): RelationshipSubmit {
  const queryClient = useQueryClient();
  const snackbars = useSnackbars();
  const { track } = useAnalytics();
  const create = useCreateRelationships();
  // Two clicks landing before a re-render would both read the pending flag as
  // false, so re-entry is blocked synchronously rather than through state.
  const inFlight = useRef(false);

  const submit = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    const { attrs } = options.editor.getModelAttributes();
    try {
      await create.mutateAsync(relationshipInputs(options.people, options.objects, attrs));
    } catch {
      snackbars.create({
        id: SNACKBAR_ID,
        variant: 'critical',
        message: 'There was an error creating relationships. Try again later.',
      });
      return;
    } finally {
      inFlight.current = false;
    }

    snackbars.create({ id: SNACKBAR_ID, message: 'Relationships added' });
    void track('objects', 'relationshipsManualAddCompleted', {
      Source: options.source,
      'Number of People': options.people.length,
      'Number of Objects': options.objects.length,
      'Number of Relationship Attributes': Object.keys(attrs).length,
    });
    queryClient.setQueryData<ObjectType[]>(objectTypeKeys.list(options.workspaceId), (types) =>
      types?.map((type) => (type.id === options.objectType.id ? updateObjectTypeSentRelAttributes(type, attrs) : type)),
    );
    options.onConfirm();
  };

  return { submit: () => void submit(), isSubmitting: create.isPending };
}
